<script>
import { mapActions } from 'vuex';
import { useAlert } from 'dashboard/composables';
import DatePicker from 'vue2-datepicker';

export default {
  name: 'CreateOverrideModal',
  props: {
    scheduleId: {
      type: String,
      default: null,
    },
    resetCreateOverride: {
      type: Function,
      default: () => {},
    },
    loadOverrides: {
      type: Function,
      default: () => {},
    }
  },
  components: {
    DatePicker,
  },
  data() {
    return {
      reason: '',
      overrideDates: [],
      timeSlots: [],
      isAvailable: true,
      isSubmitting: false,
      timeOptions: [],
      errors: {
        overrideDates: false,
        reason: false,
        timeSlots: []
      }
    };
  },
  computed: {
    today() {
      return new Date().toISOString().split('T')[0];
    }
  },
  mounted() {
    this.generateTimeOptions();
    this.addDefaultTimeSlot();
  },
  methods: {
    ...mapActions('appointments', ['scheduleOverride']),
    
    generateTimeOptions() {
      const slots = [];
      const start = 0;
      const end = 24 * 60;

      for (let minutes = start; minutes < end; minutes += 15) {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const formattedHour = hours % 12 || 12;

        slots.push({
          display: `${formattedHour}:${String(mins).padStart(2, '0')} ${ampm}`,
          value: `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:00`
        });
      }

      this.timeOptions = slots;
    },

    addDefaultTimeSlot() {
      const nineAM = this.timeOptions.find(option => 
        option.value === '09:00:00' || option.value.startsWith('09:00')
      );
      
      const fivePM = this.timeOptions.find(option => 
        option.value === '17:00:00' || option.value.startsWith('17:00')
      );
      
      if (nineAM && fivePM) {
        this.timeSlots.push({
          startTime: nineAM,
          endTime: fivePM
        });
      } else if (this.timeOptions.length >= 2) {
        this.timeSlots.push({
          startTime: this.timeOptions[0],
          endTime: this.timeOptions[this.timeOptions.length - 1]
        });
      }
    },

    removeTimeSlot(index) {
      this.timeSlots.splice(index, 1);
    },

    validateForm() {
      this.errors = {
        overrideDates: false,
        reason: false,
        timeSlots: []
      };
      
      let isValid = true;
      
      // Validate dates
      if (!this.overrideDates || this.overrideDates.length === 0) {
        this.errors.overrideDates = true;
        isValid = false;
      }

      // Validate reason
      if (!this.reason || this.reason.trim().length === 0) {
        this.errors.reason = true;
        isValid = false;
      }
      
      // Validate time slots only if available
      if (this.isAvailable) {
        this.timeSlots.forEach((slot, index) => {
          const slotErrors = {};
          
          if (!slot.startTime) {
            slotErrors.startTime = true;
            isValid = false;
          }
          
          if (!slot.endTime) {
            slotErrors.endTime = true;
            isValid = false;
          }
          
          if (slot.startTime && slot.endTime) {
            const startTime = slot.startTime.value;
            const endTime = slot.endTime.value;
            
            if (startTime >= endTime) {
              slotErrors.timeOrder = true;
              isValid = false;
            }
          }
          
          this.errors.timeSlots[index] = slotErrors;
        });
      }
      
      return isValid;
    },
    
    async handleSubmit() {
      if (!this.validateForm()) {
        useAlert(this.$t('BRANCHES.CREATE_OVERRIDE_MODAL.REVIEW_ERRORS'));
        return;
      }
      
      this.isSubmitting = true;
      
      try {
        const payloads = [];
        
        // Create payload for each date and time slot combination
        this.overrideDates.forEach(date => {
          if (this.isAvailable && this.timeSlots.length > 0) {
            // Create override with time slots
            this.timeSlots.forEach(slot => {
              payloads.push({
                override_date: date,
                start_time: slot.startTime.value,
                end_time: slot.endTime.value,
                reason: this.reason.trim()
              });
            });
          } else {
            // Create override without time slots (not available)
            payloads.push({
              override_date: date,
              start_time: null,
              end_time: null,
              reason: this.reason.trim()
            });
          }
        });

        // Submit all payloads
        for (const payload of payloads) {
          await this.scheduleOverride({ 
            scheduleId: this.scheduleId, 
            payload 
          });
        }
        
        useAlert(this.$t('BRANCHES.CREATE_OVERRIDE_MODAL.CREATE_SUCCESS'));
        this.loadOverrides(this.scheduleId);
        this.resetCreateOverride();
      } catch (err) {
        console.error('Error creating override:', err);
        useAlert(err.message || this.$t('BRANCHES.CREATE_OVERRIDE_MODAL.CREATE_ERROR'));
      } finally {
        this.isSubmitting = false;
      }
    }
  }
};
</script>

<template>
  <div class="p-6 border-2 rounded-lg bg-white dark:bg-slate-800 shadow-lg max-w-md w-full mx-auto">
    <!-- Reason -->
    <label class="block mb-5">
      <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
        {{ $t('BRANCHES.CREATE_OVERRIDE_MODAL.REASON') }} <span class="text-red-500">*</span>
      </span>
      <input 
        type="text" 
        v-model="reason" 
        :class="[
          'mt-1 w-full p-3 border rounded-md text-slate-900 dark:text-white dark:bg-slate-700 dark:border-slate-600 focus:outline-none focus:ring-2',
          errors.reason ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 focus:ring-woot-500'
        ]"
        :placeholder="$t('BRANCHES.CREATE_UNAVAILABILITY_MODAL.REASON_PLACEHOLDER')"
      />
      <p v-if="errors.reason" class="mt-1 text-sm text-red-500">
        {{ $t('BRANCHES.CREATE_OVERRIDE_MODAL.REASON_REQUIRED') }}
      </p>
    </label>

    <!-- Override Dates -->
    <label class="flex flex-col mb-5">
      <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
        Override dates <span class="text-red-500">*</span>
      </span>
      <date-picker
        class="w-full flex-1"
        v-model="overrideDates"
        multiple
        value-type="format"
        format="YYYY-MM-DD"
        :editable="false"
        :clearable="true"
        placeholder="Select dates"
        :input-class="[ 
          'mt-1 w-full p-3 border rounded-md text-slate-900 dark:text-white dark:bg-slate-700 dark:border-slate-600 focus:outline-none focus:ring-2',
          errors.overrideDates ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 focus:ring-woot-500'
        ]"
      />
      <p v-if="errors.overrideDates" class="mt-1 text-sm text-red-500">
        Date required
      </p>
    </label>

    <!-- Availability Toggle -->
    <div class="mb-5">
      <label class="flex items-center">
        <input 
          type="checkbox" 
          v-model="isAvailable"
          class="rounded border-slate-300 text-woot-600 focus:ring-woot-500 dark:border-slate-600 dark:bg-slate-700"
        />
        <span class="ml-2 text-sm font-medium text-slate-700 dark:text-slate-300">
          Mark as available
        </span>
      </label>
    </div>

    <!-- Time Slots Section (Only show if available) -->
    <div v-if="isAvailable">
      <div class="flex justify-end w-full mb-4">
        <woot-button 
          size="small"
          variant="smooth"
          @click="addDefaultTimeSlot"
        >
          {{ $t('BRANCHES.CREATE_OVERRIDE_MODAL.ADD_SLOT') }}
        </woot-button>
      </div>

      <!-- Time Slots -->
      <div 
        v-for="(slot, index) in timeSlots" 
        :key="index" 
        class="mb-8"
        :class="{'border-b border-slate-200 dark:border-slate-700 pb-4' : timeSlots.length > 1 && index < timeSlots.length - 1}"
      >
        <div class="flex items-center gap-4">
          <!-- Start Time -->
          <div class="block flex-grow">
            <span v-if="index === 0" class="text-sm font-medium text-slate-700 dark:text-slate-300">
              {{ $t('BRANCHES.CREATE_OVERRIDE_MODAL.START_TIME') }} <span class="text-red-500">*</span>
            </span>
            <div class="mt-1 relative">
              <multiselect 
                v-model="slot.startTime"
                :options="timeOptions"
                label="display"
                track-by="value"
                :searchable="true"
                :close-on-select="true"
                :show-labels="false"
                :class="[ 
                  'w-full',
                  errors.timeSlots[index] && errors.timeSlots[index].startTime ? 'multiselect-error' : ''
                ]"
                :placeholder="$t('BRANCHES.CREATE_OVERRIDE_MODAL.SELECT_START_TIME')"
              />
            </div>
            <p v-if="errors.timeSlots[index] && errors.timeSlots[index].startTime" class="mt-1 text-sm text-red-500">
              {{ $t('BRANCHES.CREATE_OVERRIDE_MODAL.START_TIME_REQUIRED') }}
            </p>
          </div>

          <span class="text-slate-500">-</span>

          <!-- End Time -->
          <div class="block flex-grow">
            <span v-if="index === 0" class="text-sm font-medium text-slate-700 dark:text-slate-300">
              {{ $t('BRANCHES.CREATE_OVERRIDE_MODAL.END_TIME') }} <span class="text-red-500">*</span>
            </span>
            <div class="mt-1 relative">
              <multiselect 
                v-model="slot.endTime"
                :options="timeOptions"
                label="display"
                track-by="value"
                :searchable="true"
                :close-on-select="true"
                :show-labels="false"
                :class="[ 
                  'w-full',
                  errors.timeSlots[index] && errors.timeSlots[index].endTime ? 'multiselect-error' : ''
                ]"
                :placeholder="$t('BRANCHES.CREATE_OVERRIDE_MODAL.SELECT_END_TIME')"
              />
            </div>
            <p v-if="errors.timeSlots[index] && errors.timeSlots[index].endTime" class="mt-1 text-sm text-red-500">
              {{ $t('BRANCHES.CREATE_OVERRIDE_MODAL.END_TIME_REQUIRED') }}
            </p>
          </div>

          <!-- Delete Button -->
          <button 
            v-if="timeSlots.length > 1"
            @click="removeTimeSlot(index)"
            type="button"
            class="self-start mt-6"
          >
            <fluent-icon 
              icon="delete" 
              class="text-red-500 hover:text-red-700 cursor-pointer h-5 w-5"
            />
          </button>
        </div>
        
        <!-- Time Order Error -->
        <p v-if="errors.timeSlots[index] && errors.timeSlots[index].timeOrder" class="mt-2 text-sm text-red-500 text-center">
          {{ $t('BRANCHES.CREATE_OVERRIDE_MODAL.TIME_ORDER_ERROR') }}
        </p>
      </div>
    </div>

    <!-- Not Available Message -->
    <div v-else class="mb-5 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
      <p class="text-sm text-yellow-700 dark:text-yellow-300">
        Unavailable
      </p>
    </div>
    
    <!-- Action Buttons -->
    <div class="flex justify-between gap-4">
      <button
        type="button"
        class="w-full py-3 text-white font-medium rounded-md transition bg-slate-600 hover:bg-slate-700 focus:ring-2 focus:ring-slate-400 focus:outline-none disabled:opacity-70 disabled:cursor-not-allowed"
        @click="resetCreateOverride()"
        :disabled="isSubmitting"
      >
        {{ $t('BRANCHES.CREATE_OVERRIDE_MODAL.CANCEL') }}
      </button>
      <button 
        type="button"
        @click="handleSubmit()"
        :disabled="isSubmitting"
        class="w-full py-3 text-white font-medium rounded-md transition 
          bg-woot-600 hover:bg-woot-700 focus:ring-2 focus:ring-woot-400 focus:outline-none
          disabled:opacity-70 disabled:cursor-not-allowed"
      >
        <span v-if="isSubmitting" class="flex items-center justify-center">
          <span class="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-white rounded-full"></span>
          {{ $t('BRANCHES.CREATE_OVERRIDE_MODAL.SUBMITTING') }}
        </span>
        <span v-else>{{ $t('BRANCHES.CREATE_OVERRIDE_MODAL.CREATE') }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.multiselect-error :deep(.multiselect__tags) {
  @apply border-red-500 ring-1 ring-red-500;
}

/* Dark mode styles for multiselect */
.dark :deep(.multiselect__tags) {
  @apply bg-slate-700 border-slate-600 text-white;
}

.dark :deep(.multiselect__input),
.dark :deep(.multiselect__single) {
  @apply bg-slate-700 text-white;
}

.dark :deep(.multiselect__content-wrapper) {
  @apply bg-slate-800 border-slate-600;
}

.dark :deep(.multiselect__option) {
  @apply text-slate-200;
}

.dark :deep(.multiselect__option--highlight) {
  @apply bg-woot-600 text-white;
}

.dark :deep(.multiselect__option--selected) {
  @apply bg-woot-700 text-white;
}
</style>